import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus, Save, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatBatchMonth } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { useCompanyOptions, useCreateFacility } from '@/features/settings/facilities/useFacilities';

import {
  dispatchRequestRouteSchema,
  type DispatchRequestDto,
  type DispatchRequestRouteInput,
} from '../types';
import { DispatchStopsSummary } from '../DispatchStopsSummary';
import { formatDispatchTime } from '../format';
import { useFacilities, useMonthlyBatchesList, useRouteRequest } from '../useDispatch';

const NS = 'operations';

interface RouteDispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DispatchRequestDto | null;
}

/**
 * Platform-admin dialog that routes a `submitted` request to a target
 * facility. The backend picks or creates the monthly batch for the request date.
 */
export const RouteDispatchDialog = ({ open, onOpenChange, request }: RouteDispatchDialogProps) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const facilities = useFacilities({ company_party_type: 'processor' });
  const batches = useMonthlyBatchesList();
  const route = useRouteRequest();
  const createFacility = useCreateFacility();
  const companies = useCompanyOptions({ party_type: 'processor' });
  const [quickFacilityOpen, setQuickFacilityOpen] = useState(false);
  const [newFacilityName, setNewFacilityName] = useState('');
  const [newFacilityCompanyId, setNewFacilityCompanyId] = useState('');
  const [ccEmails, setCcEmails] = useState('');

  const form = useForm<DispatchRequestRouteInput>({
    resolver: zodResolver(dispatchRequestRouteSchema),
    defaultValues: { facility_id: '', monthly_batch_id: '' },
  });
  const selectedFacilityId = form.watch('facility_id');
  const requestMonth = request?.requested_date?.slice(0, 7) ?? '';
  const matchingBatches = (batches.data ?? []).filter(
    (batch) =>
      (!selectedFacilityId || batch.facility_id === selectedFacilityId) &&
      (!requestMonth || batch.year_month === requestMonth),
  );

  useEffect(() => {
    if (!open) return;
    setQuickFacilityOpen(false);
    setNewFacilityName('');
    setNewFacilityCompanyId(companies.data?.[0]?.company_id ?? '');
    setCcEmails('');
    form.reset({
      facility_id: request?.routed_facility_id ?? '',
      monthly_batch_id: '',
    });
  }, [open, request, form, companies.data]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!request) return;
    await route.mutateAsync({
      id: request.dispatch_request_id,
      body: {
        facility_id: values.facility_id,
        cc_emails: ccEmails.split(',').map((email) => email.trim()).filter(Boolean),
      },
    });
    onOpenChange(false);
  });

  const onQuickCreateFacility = async () => {
    const name = newFacilityName.trim();
    if (!name) {
      form.setError('facility_id', {
        type: 'manual',
        message: t('efs.operations.dispatch.validation.facility_name_required', {
          defaultValue: '請輸入作業據點名稱',
        }),
      });
      return;
    }
    if (!newFacilityCompanyId) {
      form.setError('facility_id', {
        type: 'manual',
        message: t('efs.operations.dispatch.validation.company_required', {
          defaultValue: '請先選擇公司',
        }),
      });
      return;
    }

    try {
      const facility = await createFacility.mutateAsync({
        company_id: newFacilityCompanyId,
        facility_name: name,
        facility_role: 'receiving',
      });
      form.setValue('facility_id', facility.facility_id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setNewFacilityName('');
      setQuickFacilityOpen(false);
    } catch (err) {
      form.setError('facility_id', {
        type: 'manual',
        message: err instanceof Error ? err.message : '作業據點新增失敗',
      });
    }
  };

  const routeMonthLabel = requestMonth ? formatBatchMonth(requestMonth) : '—';
  const autoBatch = matchingBatches[0];
  const isPending = route.isPending || createFacility.isPending;
  const canCreateFacility = hasPermission(user, 'facility.create');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.dispatch.platform.route_title', {
              defaultValue: '指派處理設施',
            })}
          </DialogTitle>
          {request ? (
            <DialogDescription>
              {t(`efs.operations.dispatch.types.${request.request_type}`, {
                defaultValue: request.request_type,
              })}
              {request.requested_date ? ` · ${request.requested_date}` : ''}
              {request.requested_time ? ` ${formatDispatchTime(request.requested_time)}` : ''}
            </DialogDescription>
          ) : null}
          {request?.location ? (
            <div className="pt-2 text-left text-sm">
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                {t('efs.operations.dispatch.fields.location', { defaultValue: '場所' })}
              </p>
              <DispatchStopsSummary row={request} />
            </div>
          ) : null}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <label className="text-sm font-medium" htmlFor="route-cc-emails">CC Email</label>
              <Input id="route-cc-emails" value={ccEmails} onChange={(event) => setCcEmails(event.target.value)} placeholder="多筆請以逗號分隔" />
            </div>
            <FormField
              control={form.control}
              name="facility_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.operations.dispatch.fields.facility', {
                      defaultValue: '處理設施',
                    })}
                  </FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={facilities.isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {(facilities.data ?? []).map((f) => (
                            <SelectItem key={f.facility_id} value={f.facility_id}>
                              {f.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    {canCreateFacility ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQuickFacilityOpen((v) => !v)}
                        disabled={isPending}
                        title={t('efs.operations.dispatch.actions.quick_add_facility', {
                          defaultValue: '快速新增作業據點',
                        })}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                  {quickFacilityOpen && canCreateFacility ? (
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]">
                      <Select
                        value={newFacilityCompanyId}
                        onValueChange={setNewFacilityCompanyId}
                        disabled={isPending || companies.isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('efs.operations.dispatch.fields.company', {
                              defaultValue: '公司',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {(companies.data ?? []).map((company) => (
                            <SelectItem key={company.company_id} value={company.company_id}>
                              {company.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={newFacilityName}
                        onChange={(event) => setNewFacilityName(event.target.value)}
                        placeholder={t('efs.operations.dispatch.fields.facility_name', {
                          defaultValue: '作業據點名稱',
                        })}
                      />
                      <Button
                        type="button"
                        size="icon"
                        onClick={onQuickCreateFacility}
                        disabled={isPending}
                        title={t('efs.common.actions.save', { ns: 'common' })}
                      >
                        {createFacility.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQuickFacilityOpen(false)}
                        disabled={isPending}
                        title={t('efs.common.actions.cancel', { ns: 'common' })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="rounded-md border border-dashed border-efs-border bg-efs-bg-panel p-3 text-sm">
              <p className="font-medium text-efs-text-primary">
                {t('efs.operations.dispatch.fields.monthly_batch', {
                  defaultValue: '月合計表',
                })}
              </p>
              <p className="mt-1 text-efs-text-muted">
                {!selectedFacilityId
                  ? t('efs.operations.dispatch.platform.auto_batch_select_facility', {
                      defaultValue: '請先選擇處理作業據點。',
                    })
                  : autoBatch
                    ? t('efs.operations.dispatch.platform.auto_batch_existing', {
                        year_month: routeMonthLabel,
                        status: autoBatch.status,
                        defaultValue: `${routeMonthLabel} 月合計表會自動使用`,
                      })
                    : t('efs.operations.dispatch.platform.auto_batch_create', {
                        year_month: routeMonthLabel,
                        defaultValue: `${routeMonthLabel} 月合計表不存在時會自動建立`,
                      })}
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.operations.dispatch.actions.route', { defaultValue: '指派' })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RouteDispatchDialog;
